import type { Node } from 'reactflow';

export type AlignType =
  | 'left'
  | 'center-h'
  | 'right'
  | 'top'
  | 'center-v'
  | 'bottom';

export type DistributeAxis = 'horizontal' | 'vertical';

const isTopLevel = (n: Node) =>
  !(n as unknown as { parentNode?: string }).parentNode &&
  !(n as unknown as { parentId?: string }).parentId;

const w = (n: Node) => n.width ?? 0;
const h = (n: Node) => n.height ?? 0;

/**
 * Align every selected top-level node against the selection's bounding box.
 * Returns a new nodes array (unselected / child nodes are untouched).
 */
export function alignNodes(nodes: Node[], type: AlignType): Node[] {
  const selected = nodes.filter((n) => n.selected && isTopLevel(n));
  if (selected.length < 2) return nodes;

  const minLeft = Math.min(...selected.map((n) => n.position.x));
  const maxRight = Math.max(...selected.map((n) => n.position.x + w(n)));
  const minTop = Math.min(...selected.map((n) => n.position.y));
  const maxBottom = Math.max(...selected.map((n) => n.position.y + h(n)));
  const centerX = (minLeft + maxRight) / 2;
  const centerY = (minTop + maxBottom) / 2;

  const nextPos = (n: Node) => {
    switch (type) {
      case 'left':
        return { x: minLeft, y: n.position.y };
      case 'right':
        return { x: maxRight - w(n), y: n.position.y };
      case 'center-h':
        return { x: centerX - w(n) / 2, y: n.position.y };
      case 'top':
        return { x: n.position.x, y: minTop };
      case 'bottom':
        return { x: n.position.x, y: maxBottom - h(n) };
      case 'center-v':
        return { x: n.position.x, y: centerY - h(n) / 2 };
      default:
        return n.position;
    }
  };

  return nodes.map((n) =>
    n.selected && isTopLevel(n) ? { ...n, position: nextPos(n) } : n
  );
}

/**
 * Evenly distribute selected top-level nodes so the gaps between them are equal
 * along the given axis. The two outermost nodes stay put. Needs 3+ nodes.
 */
export function distributeNodes(nodes: Node[], axis: DistributeAxis): Node[] {
  const selected = nodes.filter((n) => n.selected && isTopLevel(n));
  if (selected.length < 3) return nodes;

  const horizontal = axis === 'horizontal';
  const size = horizontal ? w : h;
  const coord = (n: Node) => (horizontal ? n.position.x : n.position.y);

  const sorted = [...selected].sort((a, b) => coord(a) - coord(b));
  const spanStart = coord(sorted[0]);
  const spanEnd = coord(sorted[sorted.length - 1]) + size(sorted[sorted.length - 1]);
  const totalSize = sorted.reduce((sum, n) => sum + size(n), 0);
  const gap = (spanEnd - spanStart - totalSize) / (sorted.length - 1);

  const newCoord = new Map<string, number>();
  let cursor = spanStart;
  for (const n of sorted) {
    newCoord.set(n.id, cursor);
    cursor += size(n) + gap;
  }

  return nodes.map((n) => {
    if (!newCoord.has(n.id)) return n;
    const c = newCoord.get(n.id)!;
    return {
      ...n,
      position: horizontal
        ? { x: c, y: n.position.y }
        : { x: n.position.x, y: c },
    };
  });
}
